import appConstants from "../../appConstants";
import { getMinutesInHourAndMinutesStr } from "./Dispatch/RouteExecution/DriverPopUp/common/utils";
import { isPickupStop } from "./IBPlanning/Common/MergeStops/utills";
import { ALPHA_NUMERIC_REGEX } from "./Dispatch/RouteExecution/constRegex";

/* preparing special need options by combining serviceLevelList coming in client code API,
 used for show column filter options and in map popup in ibplanning/dispatch module
*/
const getSpecialNeedsOptionsWithServiceLevel = (priorityShipmentList, excludedSpecialNeeds = [], gridName='') => {
  return [
    ...priorityShipmentList.map((priorityShipment) => ({
      value: priorityShipment,
      text: priorityShipment,
      icon: appConstants.PRIORITY_SHIPMENTS_ICONS[priorityShipment] || "icon-sgs5",
      tooltip: `${priorityShipment} shipment`,
    })),
    ...appConstants.SpecialNeeds.filter((item) => {
      if (excludedSpecialNeeds.includes(item.value)) {
        return false;
      }
      if (item.allowedGrids) {
        return item.allowedGrids.includes(gridName);
      }
      if (item.notAllowedGrids) {
        return !item.notAllowedGrids.includes(gridName);
      }
      return true;
    }),
  ];
};

const getPriorityOptions = (priorityShipmentList) => {
  return priorityShipmentList.map((priorityShipment) => ({
    value: priorityShipment,
    text: priorityShipment,
    icon: appConstants.PRIORITY_SHIPMENTS_ICONS[priorityShipment] || "icon-sgs5",
    tooltip: `${priorityShipment} shipment`,
  }));
};

const getPickupTypes = (priorityShipmentList) => {
  return [
    ...priorityShipmentList.map((priorityShipment) => ({
      value: priorityShipment,
      text: priorityShipment,
      icon:
        appConstants.PRIORITY_SHIPMENTS_ICONS[priorityShipment] || "icon-sgs5",
      tooltip: `${priorityShipment}`,
    })),
    ...appConstants.PICKUP_TYPES,
    { value: '', text: '(BLANKS)' }
  ];
};

const getClosingSoonPickupTypes = (params) => {
  return [
    {
      value: appConstants.CLOSING_SOON_PICKUP_TYPES.ALERT,
      text: `Closing in < ${getMinutesInHourAndMinutesStr(
        params.context.closingSoonLimitConfig.closingSoonAlertTimeLimit
      )}`
    },
    {
      value: appConstants.CLOSING_SOON_PICKUP_TYPES.WARNING,
      text: `Closing in < ${getMinutesInHourAndMinutesStr(
        params.context.closingSoonLimitConfig.closingSoonWarningTimeLimit,
        true
      )}`
    },
    { value: appConstants.CLOSING_SOON_PICKUP_TYPES.CLOSED, text: 'Closed' },
    { value: '', text: '(BLANKS)' }
  ];
}

const getGridFeatureFlagsByGridNames = (gridConfiguration, gridName) => {
  return (
    gridConfiguration?.find((config) => config.gridName === gridName)?.value ??
    {}
  );
};

const getShipmentsDataByShipmentTypeAndFeatureFlags = (shipments, configJSON) => {
  const filteredShipmentsList = updateShipmentsDataBasedOnFeatureFlags(shipments, configJSON);
  return filteredShipmentsList.map((shipment) => {
    const { shipperLatitude, shipperLongitude, ...restShipmentData } = shipment;
    if (restShipmentData.isDelivery) {
      return restShipmentData;
    }
    return {
      ...restShipmentData,
      latitude: shipperLatitude,
      longitude: shipperLongitude,
    };
  });
};

const getStopsDataByTypeAndFeatureFlags = (stops, configJSON) => {
  const filteredStopsList = updateStopsDataBasedOnFeatureFlags(stops, configJSON);
  return filteredStopsList.map((stop) => {
    const { shipperLatitude, shipperLongitude, ...restShipmentData } = stop;
    if (isPickupStop(restShipmentData.stopType)) {
      return {
        ...restShipmentData,
        latitude: shipperLatitude,
        longitude: shipperLongitude,
      };
    }
    return restShipmentData;
  });
};

const updateStopsDataBasedOnFeatureFlags = (stopsList, configJSON) => {
  let filteredStopsList = stopsList?.slice() ?? [];
  if (!configJSON?.enableCommentsForCustomer) {
    // Remove customer and shipper comments if feature flag is disabled
    filteredStopsList = filteredStopsList.map((stop) => {
      stop.customerComments = null;
      stop.shipperComments = null;
      return stop;
    });
  }
  return filteredStopsList;
};

const updateShipmentsDataBasedOnFeatureFlags = (shipmentsList, configJSON) => {
  let filteredShipmentsList = shipmentsList?.slice() ?? [];
  if (!configJSON?.enableCommentsForCustomer) {
    // Remove customer and shipper comments if feature flag is disabled
    filteredShipmentsList = filteredShipmentsList.map((shipment) => {
      shipment.customerComments = null;
      shipment.shipperComments = null;
      return shipment;
    });
  }
  return filteredShipmentsList;
};


const getUniqueSortedPairs = (data, sortFn) => {
  return [...new Set(data)]
    .sort(sortFn)
    .map((value) => ({
      label: value,
      value: value,
    }));
};

const getUniqueSortedLabelValuePairsFromNumbers = (data) => {
  return getUniqueSortedPairs(data, (a, b) => Number(a) - Number(b));
};

const getUniqueSortedLabelValuePairsFromObjects = (data, field) => {
  const fieldValues = data.map((item) => item[field]);
  return getUniqueSortedPairs(fieldValues, (a, b) => a - b);
};

const isUnattendedStop = (stop) =>{
  return stop?.shipments?.some(shipment => shipment.isUnattendedDelivery)
}

const getExcessiveLengthCodeTooltip = ({ baseTooltip = '', excessiveLengthCode = '' } = {}) => {
  if (!excessiveLengthCode) return baseTooltip;
  return `${baseTooltip}: ${excessiveLengthCode}`;
};

// Function to get tooltip for customer & shipper comments special needs
const getCustomerShipperCommentsTooltip = (value, params) => {
  const gridName = params?.api?.gridName; // grid name is option, if present, handle tooltip for CustomerGrid/ShipperGrid separately
  const data = params.data;

  if(gridName === appConstants.gridNames.CustomerGrid){
    //Customer grid
    return data.deliveryDetails?.customerComments || null;
  }else if(gridName === appConstants.gridNames.ShipperGrid){
    //Shipper grid
    return data.pickupDetails?.shipperComments || null;
  }

  if(value === appConstants.SpecialNeedsStrings.CC && data.customerComments){
    return `Customer: ${data.customerComments}`;
  }else if(value === appConstants.SpecialNeedsStrings.SC && data.shipperComments){
    return `Shipper: ${data.shipperComments}`;
  }
  return null;
};

const getCustomerAndShipperComment = (data) => {
  const isDelivery = data.isDelivery || data.stopType === appConstants.ROUTE_STOP_TYPES.DL;

  return isDelivery
    ? {
        customerComments: data.customerComments ?? data.deliveryDetails?.customerComments,
        shipperComments: null
      }
    : {
        customerComments: null,
        shipperComments: data.shipperComments ?? data.pickupDetails?.shipperComments
      };
};


const isValidPickupNumber = ((pickupNumber) =>{
  return pickupNumber && pickupNumber !== "0" && ALPHA_NUMERIC_REGEX.test(pickupNumber);
});

export {
  getSpecialNeedsOptionsWithServiceLevel,
  getPickupTypes,
  getGridFeatureFlagsByGridNames,
  getClosingSoonPickupTypes,
  getShipmentsDataByShipmentTypeAndFeatureFlags,
  getStopsDataByTypeAndFeatureFlags,
  getUniqueSortedLabelValuePairsFromNumbers,
  getUniqueSortedLabelValuePairsFromObjects,
  isUnattendedStop,
  getExcessiveLengthCodeTooltip,
  getCustomerShipperCommentsTooltip,
  getPriorityOptions,
  getCustomerAndShipperComment,
  isValidPickupNumber
};
